import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Row, Col } from 'react-bootstrap';
import { Typography, Box } from '@material-ui/core';
import _ from 'lodash';

import { ReportsChart, StatisticCard } from './ReportsViews';
import Loader3Orange from '../../general/Loader3Orange';
import { FaFileContract, FaDollarSign, FaExclamationTriangle } from 'react-icons/fa';

// Reusing chart data preparation helpers
const preparePieData = (data, labelKey, dataKey) => {
    if (!data || data.length === 0) return null;
    const labels = data.map(item => item[labelKey]);
    const chartData = data.map(item => item[dataKey]);
    const backgroundColors = ['#4e73df', '#1cc88a', '#36b9cc', '#f6c23e', '#e74a3b', '#858796', '#5a5c69'];
    return {
        labels,
        datasets: [{
            data: chartData,
            backgroundColor: labels.map((_, i) => backgroundColors[i % backgroundColors.length]),
        }],
    };
};

const prepareBarData = (data, labelKey, dataKey, chartLabel) => {
    if (!data || data.length === 0) return null;
    const labels = data.map(item => item[labelKey]);
    const chartData = data.map(item => item[dataKey]);
    return {
        labels,
        datasets: [{
            label: chartLabel,
            data: chartData,
            backgroundColor: 'rgba(75, 192, 192, 0.6)',
            borderColor: 'rgba(75, 192, 192, 1)',
        }],
    };
};


const PolicyTab = ({ brokerInfo, startDate, endDate, selectedStatus, selectedBroker, selectedBusinessSegment, translationsDashboard }) => {
    const [loading, setLoading] = useState(true);
    const [policyStats, setPolicyStats] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        const brokerageId = brokerInfo?.information?.brokerages?.[0]?.code;
        if (!brokerageId) {
            setError("Brokerage information is not available.");
            setLoading(false);
            return;
        }

        const fetchPolicyStats = async () => {
            setLoading(true);
            setError(null);
            try {
                const token = localStorage.getItem("jwt");
                const payload = {
                    startDate: startDate,
                    endDate: endDate,
                    // Note: We might need to add insurer/vertical filters later if required
                };
                
                const response = await axios.post(
                    `${process.env.REACT_APP_BASICSERVICE}/api/policies/${brokerageId}/policies/stats`,
                    payload,
                    { headers: { "x-auth-token": token } }
                );
                
                setPolicyStats(response.data);

            } catch (err) {
                console.error("Error fetching policy stats:", err.response || err);
                setError("Failed to load policy statistics.");
            } finally {
                setLoading(false);
            }
        };

        fetchPolicyStats();

    }, [brokerInfo, startDate, endDate]); // Dependency array simplified for now

    if (loading) return <Loader3Orange />;

    if (error) {
        return (
            <Box p={3} style={{ textAlign: 'center' }}>
                <img width={"300px"} src="https://error404.fun/img/illustrations/20.png" alt="Error"/>
                <Typography color="error" variant="h6">{error}</Typography>
            </Box>
        );
    }
    
    if (!policyStats || policyStats.kpis.totalPolicies === 0) {
        return (
            <Col md={12} style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", marginTop: '5rem' }}>
                <div><img width={"400px"} src="https://error404.fun/img/illustrations/08.png" alt="No Data"/></div>
                <div><h3 style={{ fontFamily: "GibsonSemiBd", color: "rgb(63, 68, 90)" }}>{translationsDashboard.noDataFound}</h3></div>
            </Col>
        );
    }

    const premiumByInsurerData = prepareBarData(policyStats.charts.premiumByInsurer, 'name', 'count', 'Policies by Insurer');
    const premiumByVerticalData = prepareBarData(policyStats.charts.premiumByVertical, 'name', 'count', 'Policies by Vertical');
    const policiesByStatusData = preparePieData(policyStats.charts.policiesByStatus, 'name', 'count');

    return (
        <Box p={3}>
            {/* KPI Cards */}
            <Row className="mb-4">
                <Col>
                    <div style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center', gap: '20px' }}>
                        <StatisticCard cardInfo={{ icon: FaFileContract, title: 'Total Policies', data: policyStats.kpis.totalPolicies }} />
                        <StatisticCard cardInfo={{ icon: FaDollarSign, title: 'Total Premium', data: `$${(policyStats.kpis.totalPremium || 0).toLocaleString()}` }} />
                        <StatisticCard cardInfo={{ icon: FaExclamationTriangle, title: 'Near Expiry (30d)', data: policyStats.kpis.policiesNearExpiry }} />
                    </div>
                </Col>0
            </Row>
``
            {/* Charts */}
            <Row>
                <Col md={6} className="mb-4">
                     <div className="chartTitle">{'Policy Distribution by Status'}</div>
                     {policiesByStatusData ? <ReportsChart reportData={policiesByStatusData} reportChartType="DOUGHNUT" /> : <Typography>No data.</Typography>}
                </Col>
                <Col md={6} className="mb-4">
                    <div className="chartTitle">{'Top Verticals by Policy Count'}</div>
                    {premiumByVerticalData ? <ReportsChart reportData={premiumByVerticalData} reportChartType="BAR" indexAxis="y" /> : <Typography>No data.</Typography>}
                </Col>
            </Row>
            <Row>
                <Col md={12} className="mb-4">
                     <div className="chartTitle">{'Top Insurers by Policy Count'}</div>
                     {premiumByInsurerData ? <ReportsChart reportData={premiumByInsurerData} reportChartType="BAR" indexAxis="y" /> : <Typography>No data.</Typography>}
                </Col>
            </Row>
        </Box>
    );
};

export default PolicyTab;
